import React, {Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useGate, useStore} from 'effector-react';
import i18n, {TFunction} from 'i18next';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import * as yup from 'yup';

import {depositById} from 'api/billing';
import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {
    Gate,
    organizationBalanceChanged,
    pageNumberChanged,
    organizationState$,
} from 'models/profile/organizations/table';
import {BillingDebitRequest} from 'types/billing';
import {OrganizationFormattedRow, OrganizationRawRow} from 'types/profile';

import {Field, FormFields} from '../../../Register/styled';

import {BalanceContainer, FooterContainer} from './styled';

interface Props {
    configFunc: (t: TFunction) => Array<any>;
    dataMapper: (
        data: OrganizationRawRow[] | undefined,
        t: TFunction,
        setCurrentOrganization: Dispatch<SetStateAction<OrganizationRawRow | null>>,
    ) => Partial<OrganizationFormattedRow>[];
    headerControls?: ReactNode;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

type FormField = {
    name: keyof BillingDebitRequest;
};

export const OrganizationGridTable = ({
    configFunc,
    dataMapper,
    headerControls,
    exportConfig,
}: Props) => {
    const {t} = useTranslation();
    const state = useStore(organizationState$);
    useGate(Gate);

    const [currentOrganization, setCurrentOrganization] = React.useState<OrganizationRawRow | null>(
        null,
    );

    useEffect(() => {
        if (!currentOrganization) return;
        queueMicrotask(() => setValue('legalIdentityId', currentOrganization.id));
    }, [currentOrganization]);

    const translateValidates = useCallback(
        (key: string) => t(`pages.profile.billing.deposit.validations.${key}`),
        [i18n.language],
    );
    const translateForm = useCallback(
        (key: string) => t('pages.profile.billing.deposit.fields.' + key),
        [i18n.language],
    );

    const schema = yup.object().shape({
        legalIdentityId: yup
            .number()
            .required(translateValidates('required'))
            .typeError(translateValidates('numberType'))
            .positive(translateValidates('positive')),
        amount: yup
            .number()
            .required(translateValidates('required'))
            .typeError(translateValidates('numberType'))
            .positive(translateValidates('positive')),
        documentNumber: yup.string().required(translateValidates('required')),
        bankTransactionNumber: yup.string().required(translateValidates('required')),
    });

    const formFields: FormField[] = [
        {name: 'bankTransactionNumber'},
        {name: 'documentNumber'},
        {name: 'amount'},
    ];

    const {
        control,
        formState: {errors},
        handleSubmit,
        setValue,
    } = useForm<BillingDebitRequest>({
        resolver: yupResolver(schema),
        mode: 'all',
    });

    const onSubmit = async (data: BillingDebitRequest) => {
        try {
            const response = await depositById(data);
            organizationBalanceChanged(response);
            setCurrentOrganization(null);
            toast.success(t('pages.profile.billing.deposit.messages.success'));
        } catch (error) {
            console.error(error);
            toast.error(t('pages.profile.billing.deposit.messages.error'));
        }
    };

    const Footer = () => (
        <FooterContainer>
            <Button onClick={() => setCurrentOrganization(null)} variant={ButtonVariant.Outline}>
                {t('pages.profile.billing.deposit.cancel')}
            </Button>
            <Button variant={ButtonVariant.Normal} onClick={handleSubmit(onSubmit)}>
                {t('pages.profile.billing.deposit.ok')}
            </Button>
        </FooterContainer>
    );

    const columns = useMemo(() => mapTableColumns(configFunc(t)), [t]);
    const mappedData = React.useMemo(
        () => dataMapper(state.data?.data || undefined, t, setCurrentOrganization),
        [dataMapper, state, t],
    );

    return (
        <>
            <Table
                columns={columns}
                data={mappedData}
                isLoading={state.isLoading}
                totalRowsCount={state.data?.totalElements}
                pageCount={state.data?.totalPages}
                pageIndex={state.pageNumber}
                onPageChange={pageNumberChanged}
                headerControls={headerControls}
                userConfig={{
                    gridName: 'ordersGrid',
                    saveConfig: saveGridConfig,
                    getConfig: fetchGridConfig,
                }}
                exportConfig={exportConfig}
            />
            <Modal
                show={!!currentOrganization}
                onClose={() => setCurrentOrganization(null)}
                modalWidth={0}
                footer={<Footer />}
                title={t('pages.profile.billing.deposit.title')}
            >
                <BalanceContainer>
                    {t('pages.profile.billing.current')}: {currentOrganization?.balance || 0}
                </BalanceContainer>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormFields>
                        {formFields.map((field) => (
                            <Field
                                key={field.name}
                                text={errors?.[field.name]?.message}
                                isError={!!errors?.[field.name]}
                            >
                                <span>{translateForm(field.name)}</span>
                                <Controller
                                    name={field.name}
                                    control={control}
                                    defaultValue=""
                                    render={(props) => (
                                        <Input {...props} hasError={!!errors?.[field.name]} />
                                    )}
                                />
                            </Field>
                        ))}
                        <Controller
                            name="legalIdentityId"
                            control={control}
                            defaultValue=""
                            render={(props) => (
                                <input value={props.value} type="text" hidden readOnly />
                            )}
                        />
                    </FormFields>
                </form>
            </Modal>
        </>
    );
};
